import { AnimatePresence, motion } from 'framer-motion';
import { getPinnedField, sortByOrder } from '../../store/utils';
import { useIsCollapsed } from '../../hooks/use-is-collapsed';
import { CWTSchemaPropertySummary, WorksheetSchemaSection } from '../../../../../types/custom-worksheet-v2';
import { ButtonV2 } from '@cimplico/components';
import { PlusCircleIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import { selectCollapseAll } from '../../store/selectors';
import { useSelector } from 'react-redux';
import { getReference } from '../../../../../components/custom-worksheet/utils';
import FlashBackground from '../FlashBackground';
import StandaloneField from '../field/StandaloneField';
import PropertyHeader from '../PropertyHeader';
import SummaryField from './SummaryField';
import NewOrEditAdditionalField from '../field/NewOrEditAdditionalField';

type SectionProps = {
    section: WorksheetSchemaSection;
    allowRedirect?: boolean;
};

function Section({ section, allowRedirect }: SectionProps): JSX.Element {
    const defaultCollapsed = useSelector(selectCollapseAll);
    const [newAdditionalField, setNewAdditionalField] = useState(false);
    const [editAdditionalField, setEditAdditionalField] = useState<string | undefined>(undefined);
    const [isCollapsed, setIsCollapsed] = useIsCollapsed(defaultCollapsed === undefined ? false : defaultCollapsed);

    const pinnedField = getPinnedField(section);

    function handleAddField(): void {
        if (!section.additionalFields || section.additionalFields.length === 0) {
            return;
        }

        setNewAdditionalField(true);
    }

    return (
        <div id={section.id} className="scroll-mt-8 rounded-[5px] w-full relative">
            <FlashBackground id={section.id} />
            <PropertyHeader
                pinnedField={section.summary || pinnedField}
                property={section}
                isCollapsed={isCollapsed}
                onCollapse={setIsCollapsed}
                headingSize="base"
            />
            {/* Show fields table when section is expanded */}
            <AnimatePresence initial={false}>
                {!isCollapsed && (
                    <motion.div
                        exit={{ height: 0, opacity: 0 }}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        transition={{ height: { duration: 0.2 } }}
                        className="overflow-hidden"
                    >
                        <div className="pl-12 flex flex-col gap-2">
                            {sortByOrder(section.properties).map((value) => (
                                <StandaloneField
                                    allowRedirect={allowRedirect}
                                    key={value.id}
                                    field={value}
                                    headingSize="sm"
                                    isEditing={editAdditionalField === value.id}
                                    onEditing={(): void => setEditAdditionalField(editAdditionalField ? undefined : value.id)}
                                    allowedTypes={section.additionalFields || undefined}
                                />
                            ))}
                            {newAdditionalField && section.additionalFields && section.additionalFields.length > 0 && (
                                <NewOrEditAdditionalField
                                    sectionId={section.id}
                                    allowedTypes={section.additionalFields}
                                    onClose={(): void => setNewAdditionalField(false)}
                                />
                            )}
                            {section.additionalFields && section.additionalFields.length > 0 && (
                                <ButtonV2
                                    label="Add field"
                                    type="secondary"
                                    size="sm"
                                    isDisabled={newAdditionalField || editAdditionalField !== undefined}
                                    leftIcon={<PlusCircleIcon />}
                                    onClick={handleAddField}
                                />
                            )}
                            {section.summary?.properties[getReference(section.id)] && (
                                <SummaryField summary={section.summary?.properties[getReference(section.id)] as CWTSchemaPropertySummary} />
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default Section;
